import { useMemo, useState } from 'react';
import { AlertTriangle, Maximize2, X } from 'lucide-react';
import {
  type Policy,
  type PolicyWithRateLimitInfo,
} from '@mls-wallet/shared-types';
import {
  formatPolicyDetailsText,
  getPolicyTypeDisplay,
  getPolicyApprovalDisplay,
} from '@/utils/policyFormatting';
import { formatAddress } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface TransactionBuilderPolicyRowProps {
  policy: PolicyWithRateLimitInfo;
  contractAddressNameByAddress?: Map<string, string>;
  isCurrentUserInitiator?: boolean;
  onClick: () => void;
  onSeeDetails?: (policy: Policy) => void;
}

function SummaryPill({
  children,
  isHovered,
}: {
  children: React.ReactNode;
  isHovered: boolean;
}) {
  return (
    <div
      className={`px-[6px] py-0 rounded-[8px] ${
        isHovered
          ? 'bg-slate-200 dark:bg-slate-700'
          : 'bg-zinc-100 dark:bg-zinc-700'
      }`}
    >
      {children}
    </div>
  );
}

function ContractPolicySummary({
  policy,
  contractAddressNameByAddress,
  isHovered,
}: {
  policy: Extract<Policy, { transactionType: 'CONTRACT_INTERACTION' }>;
  contractAddressNameByAddress: Map<string, string> | undefined;
  isHovered: boolean;
}) {
  const contractCondition = policy.contractInteractionCondition?.[0] ?? null;
  // Policy cards only summarize the first contract condition today because
  // contract interaction policies are still modeled as a single-contract flow
  // throughout the transaction builder.
  const functionNames = contractCondition
    ? [
        ...(contractCondition.proxyFunctions?.map((f) => f.name) ?? []),
        ...(contractCondition.implementationFunctions?.map((f) => f.name) ??
          []),
      ]
    : [];
  const contractLabel = contractCondition
    ? (contractAddressNameByAddress?.get(
        contractCondition.contractAddress.toLowerCase()
      ) ?? null)
    : null;

  return (
    <div className="flex gap-[5px] items-start flex-wrap max-w-[70%]">
      <p className="font-medium text-[16px] text-black dark:text-white leading-normal">
        Call
      </p>
      <SummaryPill isHovered={isHovered}>
        <p className="font-semibold text-[16px] text-black dark:text-white leading-normal break-all">
          {functionNames.length === 1
            ? contractCondition?.proxyFunctions?.length === 1
              ? `${functionNames[0]} (proxy)`
              : functionNames[0] || 'any function'
            : functionNames.length > 1
              ? `${functionNames.length} functions`
              : 'any function'}
        </p>
      </SummaryPill>
      <p className="font-medium text-[16px] text-black dark:text-white leading-normal">
        on
      </p>
      {contractCondition ? (
        <SummaryPill isHovered={isHovered}>
          <p className="font-semibold text-[16px] text-black dark:text-white leading-normal break-all">
            {contractLabel || formatAddress(contractCondition.contractAddress)}
          </p>
        </SummaryPill>
      ) : (
        <SummaryPill isHovered={isHovered}>
          <p className="font-semibold text-[16px] text-black dark:text-white leading-normal break-all">
            any contract
          </p>
        </SummaryPill>
      )}
      {functionNames.length === 0 ? (
        <>
          <p className="font-medium text-[16px] text-black dark:text-white leading-normal">
            with
          </p>
          <SummaryPill isHovered={isHovered}>
            <p className="font-semibold text-[16px] text-black dark:text-white leading-normal break-all">
              any arguments
            </p>
          </SummaryPill>
        </>
      ) : null}
    </div>
  );
}

/**
 * Policy row component for the transaction builder
 * Displays policy information in a 4-corner layout:
 * - Top-left: Policy name
 * - Top-right: Transaction type
 * - Bottom-left: Policy details (wraps at 70% width)
 * - Bottom-right: Approval status
 *
 * On hover, shows a "See details" button and changes to slate colors
 */
export function TransactionBuilderPolicyRow({
  policy,
  contractAddressNameByAddress,
  isCurrentUserInitiator = true,
  onClick,
  onSeeDetails,
}: TransactionBuilderPolicyRowProps): React.ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const isRateLimitReached = policy.rateLimitInfo.rateLimitReached;
  const isUnavailable = !isCurrentUserInitiator;
  const isAvailableHover = isHovered && !isUnavailable;
  const rowClassName = isUnavailable
    ? 'cursor-not-allowed border-transparent bg-zinc-100 dark:border-transparent dark:bg-zinc-900'
    : 'cursor-pointer border-white bg-neutral-50 hover:bg-slate-100 dark:border-zinc-800 dark:bg-neutral-900 dark:hover:bg-slate-800';
  const detailsText = useMemo(
    () =>
      // Contract rows need a richer summary than the generic formatter because
      // we want to prefer the address-book label over the raw contract address.
      policy.transactionType === 'CONTRACT_INTERACTION'
        ? []
        : formatPolicyDetailsText(policy),
    [policy]
  );

  const handleSeeDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the row's onClick
    if (onSeeDetails) {
      onSeeDetails(policy);
    }
  };

  const handleRowClick = (e: React.MouseEvent) => {
    if (isUnavailable) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    onClick();
  };

  const row = (
    <div
      className="relative pr-[160px] -mr-[160px]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        className={`border border-solid rounded-[15px] shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] w-full transition-colors overflow-clip px-[40px] py-[27px] ${rowClassName}`}
        aria-disabled={isUnavailable}
        onClick={handleRowClick}
      >
        <div className="flex flex-col w-full items-stretch justify-start gap-[10px]">
          {/* Top row: Policy name (left) and Transaction type (right) */}
          <div className="flex items-center justify-between w-full gap-4">
            <div className="flex min-w-0 items-center gap-2">
              {isRateLimitReached ? (
                <AlertTriangle
                  className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
                  strokeWidth={2.75}
                  aria-label="Rate limit reached"
                />
              ) : null}
              <p className="font-normal text-[14px] text-neutral-500 dark:text-neutral-400 text-left truncate">
                {policy.name}
              </p>
              {isUnavailable ? (
                <span className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 text-[12px] font-semibold text-red-700 shadow-sm dark:border-red-900/70 dark:bg-red-950 dark:text-red-300">
                  <X
                    className="h-3.5 w-3.5 text-red-600 dark:text-red-300"
                    aria-hidden="true"
                  />
                  Not an initiator
                </span>
              ) : null}
            </div>
            <p
              className={`font-normal text-[14px] text-right shrink-0 ${
                isAvailableHover
                  ? 'text-slate-500 dark:text-slate-400'
                  : 'text-zinc-400 dark:text-zinc-500'
              }`}
            >
              {getPolicyTypeDisplay(policy)}
            </p>
          </div>

          {/* Bottom row: Policy details (left, max 70% width) and Approval status (right) */}
          <div className="flex items-start justify-between w-full gap-4">
            {policy.transactionType === 'CONTRACT_INTERACTION' ? (
              <ContractPolicySummary
                policy={policy}
                contractAddressNameByAddress={contractAddressNameByAddress}
                isHovered={isAvailableHover}
              />
            ) : (
              <div className="flex gap-[5px] items-start flex-wrap max-w-[70%]">
                {detailsText.map((segment, index) => {
                  if (segment.isPill) {
                    return (
                      <SummaryPill key={index} isHovered={isAvailableHover}>
                        <p className="font-semibold text-[16px] text-black dark:text-white leading-normal break-all">
                          {segment.text}
                        </p>
                      </SummaryPill>
                    );
                  } else {
                    return (
                      <p
                        key={index}
                        className="font-medium text-[16px] text-black dark:text-white leading-normal"
                      >
                        {segment.text}
                      </p>
                    );
                  }
                })}
              </div>
            )}

            {/* Approval status */}
            <p
              className={`font-normal text-[14px] text-right shrink-0 ${
                isAvailableHover
                  ? 'text-slate-600 dark:text-slate-400'
                  : 'text-zinc-500 dark:text-zinc-400'
              }`}
            >
              {getPolicyApprovalDisplay(policy)}
            </p>
          </div>
        </div>
      </button>

      {/* See details button - appears on hover */}
      {isHovered && onSeeDetails && (
        <button
          className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-zinc-200 dark:bg-zinc-700 flex gap-[10px] items-center px-[10px] py-[6px] rounded-[6px] z-10 hover:bg-zinc-300 dark:hover:bg-zinc-600 transition-colors"
          onClick={handleSeeDetailsClick}
        >
          <Maximize2 className="w-[18px] h-[18px] text-black dark:text-white" />
          <p className="font-semibold text-[14px] text-black dark:text-white">
            See details
          </p>
        </button>
      )}
    </div>
  );

  if (!isUnavailable) {
    return row;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="top" align="start">
          Only initiators for this policy can use it to create a transaction
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
